import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  useColorModeValue,
  HStack,
  Image,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '@/utils/animation';
import SectionDivider from './SectionDivider';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import {
  SiReact,
  SiPython,
  SiTelegram,
  SiNodedotjs,
  SiTypescript,
  SiTailwindcss,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiJavascript,
  SiRedis,
  SiFirebase,
  SiChakraui,
  SiGraphql,
} from 'react-icons/si';
import { FaEthereum, FaAws, FaGoogle } from 'react-icons/fa';
import { DiNodejs } from 'react-icons/di';
import { TbBrandNextjs } from 'react-icons/tb';

const MotionBox = motion(Box);
const MotionSimpleGrid = motion(SimpleGrid);

const services = [
  {
    title: 'Blockchain Development',
    description: 'Building decentralized applications (dApps) and smart contracts on various blockchain platforms.',
    image: '/blockchain.jpg',
    technologies: [FaEthereum, SiJavascript, SiNodedotjs, DiNodejs],
    features: [
      'Smart Contract Development & Auditing',
      'DeFi Protocol Implementation',
      'NFT Marketplace Development',
      'Cross-chain Integration',
    ],
  },
  {
    title: 'Full Stack Development',
    description: 'Creating modern, responsive web applications with cutting-edge technologies.',
    image: '/Full-Stack-Developer.webp',
    technologies: [SiReact, TbBrandNextjs, SiNodedotjs, SiTypescript, SiJavascript, SiChakraui, SiTailwindcss],
    features: [
      'Responsive Web Applications',
      'RESTful API Development',
      'Database Design & Integration',
      'Cloud Deployment & DevOps',
    ],
  },
  {
    title: 'AI Integration',
    description: 'Implementing AI solutions and integrating machine learning models into applications.',
    image: '/ai.jpeg',
    technologies: [SiPython, SiDocker, SiMongodb, SiPostgresql, SiRedis, SiGraphql],
    features: [
      'AI Model Integration',
      'Natural Language Processing',
      'Machine Learning Pipelines',
      'Data Analysis & Visualization',
    ],
  },
  {
    title: 'Telegram Bots & Mini Apps',
    description: 'Developing automated bots and mini applications for the Telegram platform.',
    image: '/telegramjpeg',
    technologies: [SiTelegram, SiNodedotjs, SiTypescript, SiPostgresql, FaAws, FaGoogle, SiFirebase],
    features: [
      'Custom Bot Development',
      'Mini App Creation',
      'Payment Integration',
      'Automated Workflows',
    ],
  },
];

export default function Services() {
  const { ref, isVisible } = useIntersectionObserver();
  const bgGradient = useColorModeValue(
    'linear(to-r, highlight.50, brand.50, accent.50)',
    'linear(to-r, highlight.900, brand.900, accent.900)'
  );
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.8)');
  const borderColor = useColorModeValue('brand.200', 'brand.700');
  const iconColor = useColorModeValue('brand.500', 'brand.400');
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const shadowColor = useColorModeValue('rgba(0, 163, 255, 0.1)', 'rgba(0, 163, 255, 0.2)');

  return (
    <Box
      as="section"
      py={24}
      borderTop="1px solid"
      borderColor={useColorModeValue('gray.200', 'gray.700')}
      bgGradient={useColorModeValue(
        'linear(to-bl, teal.50, cyan.50)',
        'linear(to-bl, teal.900, cyan.900)'
      )}
      boxShadow="inset 0 4px 8px rgba(0, 0, 0, 0.05)"
      id="services"
    >
      <Container maxW="7xl">
        <VStack spacing={12}>
          <Box textAlign="center" maxW="xl" mx="auto">
            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="bold"
              mb={4}
            >
              What I Do
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={textColor}
            >
              Specialized in modern web technologies and blockchain development
            </Text>
          </Box>

          <MotionSimpleGrid
            ref={ref}
            columns={{ base: 1, md: 2 }}
            spacing={8}
            w="full"
            initial="initial"
            animate={isVisible ? 'animate' : 'initial'}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <MotionBox
                key={service.title}
                variants={fadeInUp}
                bg={cardBg}
                p={8}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderColor}
                boxShadow={`0 4px 20px ${shadowColor}`}
                backdropFilter="blur(10px)"
                transition="all 0.3s ease-in-out"
                _hover={{
                  transform: 'translateY(-5px)',
                  boxShadow: `0 10px 30px ${shadowColor}`,
                  borderColor: 'accent.400',
                }}
              >
                <VStack spacing={6} align="center">
                  <Box
                    position="relative"
                    w="full"
                    h="180px"
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <Image
                      src={service.image}
                      alt={service.title}
                      objectFit="cover"
                      w="full"
                      h="full"
                      loading="lazy"
                      transform="scale(0.9)"
                    />
                  </Box>

                  <VStack spacing={3} align="center" w="full">
                    <Heading size="lg" textAlign="center">
                      {service.title}
                    </Heading>

                    <Text 
                      color={textColor}
                      textAlign="center"
                      fontSize="md"
                    >
                      {service.description}
                    </Text>
                  </VStack>

                  <HStack spacing={6} wrap="wrap" justify="center" py={2}>
                    {service.technologies.map((TechIcon, i) => (
                      <Box
                        key={i}
                        _hover={{
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease'
                        }}
                        p={2}
                      >
                        <Icon
                          as={TechIcon}
                          w={8}
                          h={8}
                          color={iconColor}
                          transition="all 0.3s ease-in-out"
                          _hover={{
                            color: 'accent.400',
                            transform: 'scale(1.1)'
                          }}
                          title={TechIcon.name.replace('Si', '')}
                        />
                      </Box>
                    ))}
                  </HStack>

                  <VStack align="start" spacing={3} w="full" pl={4}>
                    {service.features.map((feature, i) => (
                      <HStack key={i} spacing={3}>
                        <Box
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg={iconColor}
                        />
                        <Text
                          fontSize="md"
                          color={textColor}
                        >
                          {feature}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </MotionBox>
            ))}
          </MotionSimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
}
